import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { defaultFieldNameSelector, type TStatus } from "@package/grid-utils";
import { useGraphQLClient, useSubscribe } from "@package/graphql/react";
import { useCallback, useMemo, useRef } from "react";
import { nanoid } from "nanoid";

import type { PageProductsFragmentFragment } from "../../__generated__/gql/graphql";
import { captureEvent } from "../../utils/o11y";
import { createQueryCacheHook } from "../../context/query";
import { readFragment } from "../../graphql";
import { compareIdx, isListOnlyPermissions, utcNow } from "../../webapp/utils";
import { type TProduct, type TStatusMap, useProductsByIds } from "../products";
import {
  AllPageProductsQuery,
  PageProductInsertMany,
  PageProductsFragment,
  PageProductsHardDelete,
  PageProductUpdateMany,
  PageProductUpdateSub,
} from "./queries";
import { useUserId } from "../../context/auth/hooks";

export type TPageProduct = PageProductsFragmentFragment;

export const usePageProductsQueryCache = createQueryCacheHook<TPageProduct[]>(["page_products"]);

/**
 * Fold a `page_products` live-query tail into the cached list.
 * `undefined` means the initial query has not landed. That tail is rows changed since
 * subscribe — often nothing — and must not seed the cache, or the market grid treats
 * the page as empty until a later refetch.
 */
export const mergePageProductTail = (
  existing: readonly TPageProduct[] | undefined,
  incoming: readonly TPageProduct[],
): TPageProduct[] | undefined => {
  if (!existing) return undefined;
  const newItems = incoming.filter((x) => !existing.find((y) => y.id === x.id));
  const updated = existing.map((x) => {
    const next = incoming.find((y) => y.id === x.id);
    if (!next) return x;
    if (!x.updated_at || !next.updated_at) return next;
    if (next.updated_at > x.updated_at) return next;
    return x;
  });
  return [...updated, ...newItems].sort(compareIdx);
};

export const usePageProductsQueryOptions = (userId: string) => {
  const hasura = useGraphQLClient();
  const cached = usePageProductsQueryCache();
  return queryOptions({
    enabled: !!userId,
    queryKey: cached.queryKey,
    queryFn: async () => {
      const res = await hasura.execute(AllPageProductsQuery);
      const all = readFragment(PageProductsFragment, res.data?.page_products ?? []).toSorted(compareIdx);

      // Hard delete any soft deleted products. We only soft delete so that delete updates are broadcast to the subscribers.
      const removed = all.filter((p) => p.deleted_at);
      if (removed.length > 0) {
        await hasura.execute(PageProductsHardDelete, {
          products: removed.map((p) => p.id),
        });
      }
      return all;
    },
  });
};

export const useSyncPageProducts = () => {
  const cached = usePageProductsQueryCache();
  const since = useMemo(() => utcNow().toISOString(), []);

  useSubscribe({
    document: PageProductUpdateSub,
    variables: { since },
    next: (res) => {
      const incoming = readFragment(PageProductsFragment, res.data?.page_products ?? []);
      const merged = mergePageProductTail(cached.get(), incoming);
      if (merged) cached.set(merged);
    },
  });
};

export const usePageProducts = (pageId?: string) => {
  const userId = useUserId();
  return useQuery({
    ...usePageProductsQueryOptions(userId),
    select: (data) => data?.filter((p) => !p.deleted_at && p.page_id === pageId).sort(compareIdx),
  });
};

export const usePageProductIds = (pageId?: string) => {
  const userId = useUserId();
  return useQuery({
    ...usePageProductsQueryOptions(userId),
    select: (data) =>
      data
        ?.filter((p) => !p.deleted_at)
        .filter((p) => (pageId ? p.page_id === pageId : true))
        .map((p) => p.product_id)
        .filter(Boolean),
  });
};

export const useAllPageProducts = (userId: string) => useQuery(usePageProductsQueryOptions(userId));

export type TPageProductWithProduct = TProduct & TPageProduct;

export const usePageProductsWithInfo = (pageId?: string): TPageProductWithProduct[] => {
  const pageProducts = usePageProducts(pageId);
  const ids = (pageProducts.data ?? []).map((p) => p.product_id).filter(Boolean);
  const allProducts = useProductsByIds(ids);
  return useMemo(() => {
    return (pageProducts.data ?? [])
      .filter((p) => !p.deleted_at && p.page_id === pageId)
      .sort(compareIdx)
      .map((x) => {
        const info = allProducts.data?.find((p) => p?.id === x.product_id);
        if (!info) return null;
        return { ...info, ...x };
      })
      .filter(Boolean);
  }, [pageProducts.data, allProducts.data, pageId]);
};

export const usePageProduct = (pageId: string, productId: string) =>
  useQuery({
    ...usePageProductsQueryOptions(pageId),
    select: (data) => data?.find((p) => p.id === productId),
  });

export const usePageProductByGridId = (pageId: string, gridId: string) =>
  useQuery({
    ...usePageProductsQueryOptions(pageId),
    select: (data) => data?.find((p) => p.grid_id === gridId),
  });

export const useSelectedPageProducts = (pageId: string, ids: string[]) =>
  useQuery({
    ...usePageProductsQueryOptions(pageId),
    select: (data) => data?.filter((p) => ids.includes(p.id)),
  });

export const useUpdatePageProducts = () => {
  const cached = usePageProductsQueryCache();
  const hasura = useGraphQLClient();
  const qry = useQueryClient();

  const pending = useRef<Map<string, Partial<TPageProduct>>>(new Map());
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const mutation = useMutation({
    networkMode: "always",
    mutationFn: async () => {
      const changes = Array.from(pending.current.entries());
      pending.current.clear();

      const updates = changes.map(([id, v]) => {
        return {
          where: { id: { _eq: id } },
          _set: v,
        };
      });
      await hasura.execute(PageProductUpdateMany, { updates });
    },
  });

  const optimistic = useCallback(
    async (values: Partial<TPageProduct> & { id: string }) => {
      const next = { ...values, updated_at: utcNow().toISOString() };

      pending.current.set(next.id, {
        ...pending.current.get(next.id),
        ...next,
      });
      cached.set((cached.get() ?? []).map((x) => (x.id === next.id ? { ...x, ...pending.current.get(next.id) } : x)));

      if (timeout.current) clearTimeout(timeout.current);

      await qry.cancelQueries({ queryKey: ["page_products"] });

      timeout.current = setTimeout(
        () => mutation.mutate(),
        750, // debounce to batch updates
      );
    },
    [qry, mutation, cached],
  );

  return { optimistic, mutation };
};

export const useDuplicatePageProducts = () => {
  const cached = usePageProductsQueryCache();
  const hasura = useGraphQLClient();
  const qry = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async (arg: { pageId: string; newPageId: string }) => {
      const products = cached.get()?.filter((p) => p.page_id === arg.pageId);
      if (!products) return Promise.reject("No page products found");

      const newProducts = products.map((p) => {
        const id = nanoid();
        return {
          ...p,
          updated_at: utcNow().toISOString(),
          id,
          page_id: arg.newPageId,
        };
      });

      // Optimistically add new products to the cache immediately
      const existingProducts = cached.get() ?? [];
      cached.set([...existingProducts, ...newProducts]);

      return await hasura.execute(PageProductInsertMany, {
        objects: newProducts,
      });
    },
    onSettled: () => {
      qry.invalidateQueries({ queryKey: ["pages"] });
      qry.invalidateQueries({ queryKey: ["page_products"] });
    },
  });
};

export const useInsertPageProducts = () => {
  const cached = usePageProductsQueryCache();
  const hasura = useGraphQLClient();

  return useMutation({
    networkMode: "always",
    mutationFn: (prods: TPageProduct[]) => {
      const objects = prods.map((p) => ({
        ...p,
        updated_at: utcNow().toISOString(),
      }));
      cached.set([...(cached.get() ?? []), ...objects].sort(compareIdx));
      return hasura.execute(PageProductInsertMany, { objects });
    },
    onSuccess: (_, products) => {
      products.forEach((p) => {
        captureEvent("inserted_column", {
          type: p.column_type,
          pageId: p.page_id,
          productId: p.product_id ?? undefined,
        });
      });
    },
  });
};

export const useDeletePageProducts = () => {
  const updated = useUpdatePageProducts();

  return useMutation({
    mutationFn: (id: string) => updated.optimistic({ id, deleted_at: utcNow().toISOString() }),
  });
};

export type TPageProductWithProductAndInfo = TPageProductWithProduct & {
  columnId: string;
  selector: string;
  status: TStatus;
  eodId: string | undefined | null;
  isPermissioned: boolean;
  hasSharedCell: boolean;
};

export function addInfoToPageProduct(
  product: TPageProductWithProduct,
  statusMap: TStatusMap,
): TPageProductWithProductAndInfo | undefined {
  if (!product || !product.product_id || !product.artis_type) return;

  const artisType = product?.artis_type;
  const fieldSelector = product?.column_field_selector ?? defaultFieldNameSelector(artisType || "customer_curve");

  const permissions = product?.packageByPackage?.permissions;
  const isListOnlyPermission = isListOnlyPermissions(permissions);

  const isPermissioned = permissions?.length && !isListOnlyPermission;

  const eodId = artisType === "eod" ? product.eod_product_dep || product.product_id : product.eod_product_dep;

  return {
    ...product,
    columnId: product.grid_id ?? product.id,
    eodId,
    selector: fieldSelector,
    isPermissioned: !!isPermissioned,
    hasSharedCell: !!product.has_shared_cell,
    status: statusMap?.[product.product_id] || "listen",
  };
}
